import type {
  OpportunityAssessmentApiResponse,
  OpportunityAssessmentDecisionStatus,
  OpportunityAssessmentIntelligenceApiResponse,
  OpportunityAssessmentPatchRequest,
  OpportunityAssessmentFollowUpActionApiResponse,
  OpportunityAssessmentFollowUpActionPatchRequest,
  OpportunityAssessmentParticipantApiResponse,
  OpportunityAssessmentParticipantPatchRequest,
  OpportunityBidRisksApiResponse,
  OpportunityValueAssessmentApiResponse,
} from '../../features/opportunities/opportunity-wizard/steps/opportunity-assessment/opportunity-assessment-api/opportunity-assessment-api.models';
import { applyOpportunityAssessmentMockDefaults } from './opportunity-assessment.mock-fixtures';
import { mockMomentumUserFromOId } from './users-api-mock.fixtures';

const ASSESSMENT_GET_PATH = /^\/api\/opportunities\/([^/]+)\/assessment$/;
const ASSESSMENT_PATCH_PATH = /^\/api\/opportunities\/([^/]+)\/assessment$/;
const RISKS_GET_PATH = /^\/api\/opportunities\/([^/]+)\/assessment\/risks$/;
const INTELLIGENCE_GET_PATH = /^\/api\/opportunities\/([^/]+)\/assessment\/intelligence$/;
const INTELLIGENCE_ANALYZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/assessment\/intelligence\/analyze$/;
const INTELLIGENCE_FINALIZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/assessment\/intelligence\/finalize$/;
const OPPORTUNITY_VALUE_GET_PATH = /^\/api\/opportunities\/([^/]+)\/assessment\/opportunity-value$/;
const OPPORTUNITY_VALUE_ANALYZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/assessment\/opportunity-value\/analyze$/;
const BID_RISK_ANALYZE_PATH = /^\/api\/opportunities\/([^/]+)\/assessment\/risks\/analyze$/;

const assessmentByOpportunityId = new Map<string, OpportunityAssessmentApiResponse>();
const risksByOpportunityId = new Map<string, OpportunityBidRisksApiResponse>();
const intelligenceByOpportunityId = new Map<string, OpportunityAssessmentIntelligenceApiResponse>();
const valuePollCountByOpportunityId = new Map<string, number>();

function nowIso(): string {
  return new Date().toISOString();
}

/** Applies a PATCH field when present on the body (null clears nullable values). */
function patchField<T>(bodyValue: T | undefined, currentValue: T): T {
  return bodyValue !== undefined ? bodyValue : currentValue;
}

function buildAssessment(opportunityId: string): OpportunityAssessmentApiResponse {
  const existing = assessmentByOpportunityId.get(opportunityId);
  if (existing) {
    return existing;
  }
  const created = nowIso();
  const shell: OpportunityAssessmentApiResponse = {
    opportunityId,
    createdAtUtc: created,
    updatedAtUtc: created,
    decisionStatus: 'Pending',
    areRisksAcknowledged: false,
  };
  const withDefaults = applyOpportunityAssessmentMockDefaults(shell);
  assessmentByOpportunityId.set(opportunityId, withDefaults);
  return withDefaults;
}

function buildRisks(opportunityId: string): OpportunityBidRisksApiResponse {
  const existing = risksByOpportunityId.get(opportunityId);
  if (existing) {
    return existing;
  }
  const bundle: OpportunityBidRisksApiResponse = {
    opportunityId,
    status: 'Completed',
    lastUpdatedAtUtc: nowIso(),
    riskCount: 2,
    risks: [
      {
        id: `mock-risk-${opportunityId}-1`,
        opportunityId,
        title: 'Authority gap',
        description:
          'Decision-maker not yet engaged; procurement committee sign-off required for this deal size.',
        category: 'Governance',
        severity: 'Amber',
        mitigation: 'Schedule executive briefing before submission.',
        generatedAtUtc: nowIso(),
      },
      {
        id: `mock-risk-${opportunityId}-2`,
        opportunityId,
        title: 'Aggressive timeline',
        description:
          'RFP submission window leaves limited time for solution design and pricing validation.',
        category: 'Timeline',
        severity: 'Red',
        generatedAtUtc: nowIso(),
      },
    ],
  };
  risksByOpportunityId.set(opportunityId, bundle);
  return bundle;
}

/** Marks bid-risk analysis as running after SCOTSMAN finalize (mock pipeline). */
export function mockMarkBidRiskRunning(opportunityId: string): void {
  const timestamp = nowIso();
  risksByOpportunityId.set(opportunityId, {
    opportunityId,
    status: 'Running',
    startedAtUtc: timestamp,
    lastUpdatedAtUtc: timestamp,
    riskCount: 0,
    risks: [],
  });
}

export function mockResetOpportunityValuePoll(opportunityId: string): void {
  valuePollCountByOpportunityId.set(opportunityId, 0);
}

export function mockStartIntelligenceAnalysis(opportunityId: string): void {
  const timestamp = nowIso();
  intelligenceByOpportunityId.set(opportunityId, {
    opportunityId,
    status: 'Running',
    startedAtUtc: timestamp,
    lastUpdatedAtUtc: timestamp,
  });
}

function buildCompletedIntelligence(
  opportunityId: string,
): OpportunityAssessmentIntelligenceApiResponse {
  const timestamp = nowIso();
  return {
    opportunityId,
    status: 'Completed',
    startedAtUtc: timestamp,
    completedAtUtc: timestamp,
    lastUpdatedAtUtc: timestamp,
    // Mirrors a real run: the scores were computed from whichever bid risks are current, so the OA step treats them
    // as up to date. A later re-analysis moves the risks on and this run reads as owing a re-run until it rebuilds.
    bidRiskLastUpdatedAtUtc: buildRisks(opportunityId).lastUpdatedAtUtc,
    attractiveScore: 72,
    winnableScore: 55,
    attractiveReasons: [
      { polarity: 'Positive', text: 'Strong account potential and deal size fit.' },
      { polarity: 'Negative', text: 'Limited prior delivery history on this scope.' },
    ],
    winnableReasons: [
      { polarity: 'Positive', text: 'Differentiated cloud and data platform credentials.' },
      { polarity: 'Negative', text: 'Incumbent supplier relationship may slow access.' },
    ],
    challengerNote: {
      severity: 'Amber',
      text: 'Confirm economic buyer access before final bid commit.',
    },
  };
}

function resolveIntelligence(opportunityId: string): OpportunityAssessmentIntelligenceApiResponse {
  const existing = intelligenceByOpportunityId.get(opportunityId);
  if (!existing) {
    return {
      opportunityId,
      status: 'NotStarted',
      lastUpdatedAtUtc: nowIso(),
    };
  }
  if (existing.status === 'Running') {
    const completed = buildCompletedIntelligence(opportunityId);
    intelligenceByOpportunityId.set(opportunityId, completed);
    return completed;
  }
  return existing;
}

function mapParticipantPatch(
  patch: OpportunityAssessmentParticipantPatchRequest,
): OpportunityAssessmentParticipantApiResponse {
  return {
    user: mockMomentumUserFromOId(patch.userOId),
    role: patch.role ?? null,
  };
}

function mapFollowUpActionPatch(
  action: OpportunityAssessmentFollowUpActionPatchRequest,
): OpportunityAssessmentFollowUpActionApiResponse {
  return {
    description: action.description ?? null,
    owner: mockMomentumUserFromOId(action.ownerOId),
    dueDateUtc: action.dueDateUtc ?? null,
  };
}

function applyPatch(
  current: OpportunityAssessmentApiResponse,
  body: OpportunityAssessmentPatchRequest,
): OpportunityAssessmentApiResponse {
  const merged: OpportunityAssessmentApiResponse = {
    ...current,
    updatedAtUtc: nowIso(),
    qualifyDateUtc: body.qualifyDateUtc ?? current.qualifyDateUtc,
    alphaDateUtc: body.alphaDateUtc ?? current.alphaDateUtc,
    approvalGateDateUtc: body.approvalGateDateUtc ?? current.approvalGateDateUtc,
    submissionDateUtc: body.submissionDateUtc ?? current.submissionDateUtc,
    closeDateUtc: body.closeDateUtc ?? current.closeDateUtc,
    recommendation: patchField(body.recommendation, current.recommendation),
    additionalNotes: patchField(body.additionalNotes, current.additionalNotes),
    decisionStatus: (body.decisionStatus ??
      current.decisionStatus) as OpportunityAssessmentDecisionStatus,
    discussionNotes: patchField(body.discussionNotes, current.discussionNotes),
    areRisksAcknowledged: body.areRisksAcknowledged ?? current.areRisksAcknowledged,
    participants: body.participants
      ? body.participants.map(mapParticipantPatch)
      : current.participants,
    followUpActions: body.followUpActions
      ? body.followUpActions.map(mapFollowUpActionPatch)
      : current.followUpActions,
  };
  return applyOpportunityAssessmentMockDefaults(merged);
}

export function mockHandleOpportunityAssessmentGet(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = ASSESSMENT_GET_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }
  return {
    status: 200,
    body: applyOpportunityAssessmentMockDefaults(buildAssessment(opportunityId)),
  };
}

export function mockHandleOpportunityAssessmentPatch(
  canonical: string,
  body: unknown,
): { status: number; body: unknown } {
  const match = ASSESSMENT_PATCH_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }
  const patch = (body ?? {}) as OpportunityAssessmentPatchRequest;
  const updated = applyPatch(buildAssessment(opportunityId), patch);
  assessmentByOpportunityId.set(opportunityId, updated);
  return { status: 200, body: applyOpportunityAssessmentMockDefaults(updated) };
}

export function mockHandleOpportunityBidRisksGet(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = RISKS_GET_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }
  return { status: 200, body: buildRisks(opportunityId) };
}

export function mockHandleOpportunityIntelligenceGet(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = INTELLIGENCE_GET_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }
  return { status: 200, body: resolveIntelligence(opportunityId) };
}

export function mockHandleOpportunityIntelligenceAnalyze(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = INTELLIGENCE_ANALYZE_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }
  const timestamp = nowIso();
  const running: OpportunityAssessmentIntelligenceApiResponse = {
    opportunityId,
    status: 'Running',
    startedAtUtc: timestamp,
    lastUpdatedAtUtc: timestamp,
  };
  intelligenceByOpportunityId.set(opportunityId, running);
  return { status: 202, body: { correlationId: `mock-intelligence-${opportunityId}` } };
}

export function mockHandleOpportunityIntelligenceFinalize(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = INTELLIGENCE_FINALIZE_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }
  mockStartIntelligenceAnalysis(opportunityId);
  return { status: 200, body: resolveIntelligence(opportunityId) };
}

export function mockHandleOpportunityValueAnalyze(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = OPPORTUNITY_VALUE_ANALYZE_PATH.exec(canonical);
  if (!match?.[1]) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  valuePollCountByOpportunityId.set(opportunityId, 0);
  return { status: 202, body: { correlationId: `mock-value-${opportunityId}` } };
}

export function mockHandleBidRiskAnalyze(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = BID_RISK_ANALYZE_PATH.exec(canonical);
  if (!match?.[1]) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  mockMarkBidRiskRunning(opportunityId);
  return { status: 202, body: { correlationId: `mock-bid-risk-${opportunityId}` } };
}

export function mockHandleOpportunityValueGet(canonical: string): {
  status: number;
  body: unknown;
} {
  const match = OPPORTUNITY_VALUE_GET_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }
  const opportunityId = match[1];
  if (!opportunityId) {
    return { status: 404, body: null };
  }

  const pollCount = (valuePollCountByOpportunityId.get(opportunityId) ?? 0) + 1;
  valuePollCountByOpportunityId.set(opportunityId, pollCount);
  const timestamp = nowIso();

  if (pollCount < 2) {
    const running: OpportunityValueAssessmentApiResponse = {
      opportunityId,
      status: 'Running',
      lastUpdatedAtUtc: timestamp,
    };
    return { status: 200, body: running };
  }

  const response: OpportunityValueAssessmentApiResponse = {
    opportunityId,
    status: 'Completed',
    lastUpdatedAtUtc: timestamp,
    currency: 'CHF',
    estimatedTcv: 890_000,
    lowEstimate: 850_000,
    highEstimate: 950_000,
    estimateBasis: 'CustomerEvidence',
    customerStatedValue: 900_000,
    confidence: 'Medium',
    rationale:
      'The RFP states a procurement budget of CHF 850k–950k for a 12-month delivery programme. ' +
      'Scope aligns with four FTE over 12 months at standard day rates, supporting a mid-range TCV of CHF 890k.',
  };
  return { status: 200, body: response };
}
